import { BubbleProvider } from "./js/bubbleProvider.js";
import { utility } from "./js/utility.js";

let provider;
let selectedUser = null;
let selectedCompany = null;

// Custom Select Component Class
class CustomSelect {
  constructor(originalSelect) {
    this.originalSelect = originalSelect;
    this.options = [];
    this.filteredOptions = [];
    this.isOpen = false;
    this.selectedValue = '';
    this.selectedText = '';
    this.searchTerm = '';
    
    this.init();
  }

  init() {
    this.createCustomSelect();
    this.bindEvents();
    this.updateFromOriginal();
    // Store instance reference on both elements
    this.originalSelect._customSelectInstance = this;
    this.customSelect._customSelectInstance = this;
  }

  createCustomSelect() {
    // Hide original select
    this.originalSelect.classList.add('original-select');
    
    // Create custom select container
    this.customSelect = document.createElement('div');
    this.customSelect.className = 'custom-select';
    
    // Create trigger
    this.trigger = document.createElement('div');
    this.trigger.className = 'custom-select-trigger placeholder';
    this.trigger.setAttribute('tabindex', '0');
    this.trigger.setAttribute('role', 'combobox');
    this.trigger.setAttribute('aria-expanded', 'false');
    
    // Create selected text span
    this.selectedTextSpan = document.createElement('span');
    this.trigger.appendChild(this.selectedTextSpan);
    
    // Create arrow
    this.arrow = document.createElement('div');
    this.arrow.className = 'custom-select-arrow';
    this.trigger.appendChild(this.arrow);
    
    // Create dropdown
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'custom-select-dropdown';
    
    // Create search container
    this.searchContainer = document.createElement('div');
    this.searchContainer.className = 'custom-select-search';
    
    // Create search wrapper
    this.searchWrapper = document.createElement('div');
    this.searchWrapper.className = 'custom-select-search-wrapper';
    
    // Create search input
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.placeholder = 'Search...';
    this.searchWrapper.appendChild(this.searchInput);
    
    // Create search clear button
    this.searchClearBtn = document.createElement('button');
    this.searchClearBtn.className = 'custom-select-search-clear';
    this.searchClearBtn.innerHTML = '×';
    this.searchClearBtn.setAttribute('aria-label', 'Clear search');
    this.searchClearBtn.setAttribute('type', 'button');
    this.searchWrapper.appendChild(this.searchClearBtn);
    
    this.searchContainer.appendChild(this.searchWrapper);
    this.dropdown.appendChild(this.searchContainer);
    
    // Create options container
    this.optionsContainer = document.createElement('div');
    this.dropdown.appendChild(this.optionsContainer);
    
    // Assemble
    this.customSelect.appendChild(this.trigger);
    this.customSelect.appendChild(this.dropdown);
    
    // Insert after original select and store instance reference
    this.originalSelect.parentNode.insertBefore(this.customSelect, this.originalSelect.nextSibling);
    this.customSelect.customInstance = this;
  }

  bindEvents() {
    // Trigger click
    this.trigger.addEventListener('click', (e) => {
      console.log('Custom select clicked', this.originalSelect.id);
      this.toggle();
    });

    // Search input
    this.searchInput.addEventListener('input', (e) => {
      this.searchTerm = e.target.value.toLowerCase();
      this.filterOptions();
      
      // Show/hide search clear button
      if (this.searchTerm) {
        this.searchContainer.classList.add('has-value');
      } else {
        this.searchContainer.classList.remove('has-value');
      }
    });

    // Search clear button
    this.searchClearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.clearSearch();
    });

    // Keyboard navigation
    this.trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.open();
        this.highlightNext();
      } else if (e.key === 'Escape') {
        this.close();
      }
    });

    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.highlightNext();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.highlightPrevious();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const highlighted = this.optionsContainer.querySelector('.highlighted');
        if (highlighted) {
          highlighted.click();
        }
      } else if (e.key === 'Escape') {
        this.close();
      }
    });

    // Close on outside click - use capture to ensure it runs before other clicks
    document.addEventListener('click', (e) => {
      if (!this.customSelect.contains(e.target)) {
        this.close();
      }
    }, true);
  }

  toggle() {
    console.log('Toggle called, isOpen:', this.isOpen, 'dropdown exists:', !!this.dropdown);
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    console.log('Opening dropdown for:', this.originalSelect.id);
    
    // Close other selects by calling their close methods
    document.querySelectorAll('.custom-select.open').forEach(selectEl => {
      if (selectEl !== this.customSelect) {
        const instance = selectEl._customSelectInstance;
        if (instance) {
          instance.close();
        }
      }
    });

    this.isOpen = true;
    this.customSelect.classList.add('open');
    this.trigger.classList.add('open');
    this.dropdown.classList.add('open');
    this.trigger.setAttribute('aria-expanded', 'true');
    
    console.log('Dropdown classes after open:', this.customSelect.className, this.dropdown.className);
    
    // Focus search input after a small delay to ensure dropdown is visible
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
      this.filterOptions();
    }, 10);
  }

  close() {
    this.isOpen = false;
    this.customSelect.classList.remove('open');
    this.trigger.classList.remove('open');
    this.dropdown.classList.remove('open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.clearHighlight();
  }

  clearSearch() {
    this.searchTerm = '';
    this.searchInput.value = '';
    this.searchContainer.classList.remove('has-value');
    this.filterOptions();
  }

  selectOption(value, text) {
    this.selectedValue = value;
    this.selectedText = text;
    this.originalSelect.value = value;
    this.updateTrigger();
    
    // Clear search and restore original list when option is selected
    this.clearSearch();
    
    this.close();
    this.triggerChangeEvent();
  }

  updateTrigger() {
    if (this.selectedValue) {
      this.selectedTextSpan.textContent = this.selectedText;
      this.trigger.classList.remove('placeholder');
    } else {
      this.selectedTextSpan.textContent = this.originalSelect.options[0]?.text || 'Select...';
      this.trigger.classList.add('placeholder');
    }
  }

  updateFromOriginal() {
    this.options = [];
    for (let i = 0; i < this.originalSelect.options.length; i++) {
      const option = this.originalSelect.options[i];
      this.options.push({
        value: option.value,
        text: option.textContent,
        disabled: option.disabled
      });
    }
    
    this.filteredOptions = [...this.options];
    
    // Update selected state
    this.selectedValue = this.originalSelect.value;
    this.selectedText = this.originalSelect.options[this.originalSelect.selectedIndex]?.text || '';
    this.updateTrigger();
    this.renderOptions();
  }

  filterOptions() {
    if (!this.searchTerm) {
      this.filteredOptions = [...this.options];
    } else {
      this.filteredOptions = this.options.filter(option => 
        option.text.toLowerCase().includes(this.searchTerm)
      );
    }
    this.renderOptions();
  }

  renderOptions() {
    this.optionsContainer.innerHTML = '';
    
    if (this.filteredOptions.length === 0) {
      const noResults = document.createElement('div');
      noResults.className = 'custom-select-option no-results';
      noResults.textContent = 'No results found';
      this.optionsContainer.appendChild(noResults);
      return;
    }

    this.filteredOptions.forEach((option, index) => {
      const optionEl = document.createElement('div');
      optionEl.className = 'custom-select-option';
      optionEl.textContent = option.text;
      optionEl.setAttribute('data-value', option.value);
      optionEl.setAttribute('data-index', index);
      
      if (option.value === this.selectedValue) {
        optionEl.classList.add('selected');
      }
      
      if (option.disabled || option.value === '') {
        optionEl.classList.add('disabled');
        optionEl.setAttribute('aria-disabled', 'true');
        if (option.value === '') {
          optionEl.classList.add('placeholder-option');
        }
      } else {
        optionEl.addEventListener('click', () => {
          this.selectOption(option.value, option.text);
        });
        
        optionEl.addEventListener('mouseenter', () => {
          this.clearHighlight();
          optionEl.classList.add('highlighted');
        });
      }
      
      this.optionsContainer.appendChild(optionEl);
    });
  }

  highlightNext() {
    const options = this.optionsContainer.querySelectorAll('.custom-select-option:not(.disabled):not(.no-results)');
    const highlighted = this.optionsContainer.querySelector('.highlighted');
    let nextIndex = 0;
    
    if (highlighted) {
      const currentIndex = parseInt(highlighted.getAttribute('data-index'));
      nextIndex = currentIndex + 1;
      if (nextIndex >= options.length) nextIndex = 0;
    }
    
    this.clearHighlight();
    options[nextIndex]?.classList.add('highlighted');
    options[nextIndex]?.scrollIntoView({ block: 'nearest' });
  }

  highlightPrevious() {
    const options = this.optionsContainer.querySelectorAll('.custom-select-option:not(.disabled):not(.no-results)');
    const highlighted = this.optionsContainer.querySelector('.highlighted');
    let prevIndex = options.length - 1;
    
    if (highlighted) {
      const currentIndex = parseInt(highlighted.getAttribute('data-index'));
      prevIndex = currentIndex - 1;
      if (prevIndex < 0) prevIndex = options.length - 1;
    }
    
    this.clearHighlight();
    options[prevIndex]?.classList.add('highlighted');
    options[prevIndex]?.scrollIntoView({ block: 'nearest' });
  }

  clearHighlight() {
    const highlighted = this.optionsContainer.querySelector('.highlighted');
    if (highlighted) {
      highlighted.classList.remove('highlighted');
    }
  }

  triggerChangeEvent() {
    const event = new Event('change', { bubbles: true });
    this.originalSelect.dispatchEvent(event);
  }

  // Public methods
  setValue(value) {
    const option = this.options.find(opt => opt.value === value);
    if (option) {
      this.selectOption(value, option.text);
    }
  }

  getValue() {
    return this.selectedValue;
  }

  destroy() {
    this.customSelect.remove();
    this.originalSelect.classList.remove('original-select');
  }
}

// Initialize custom selects for all select elements
function initializeCustomSelects() {
  const selectElements = document.querySelectorAll('select');

  selectElements.forEach((select, index) => {
    if (!select.classList.contains('original-select')) {
      
      try {
        const customSelect = new CustomSelect(select);
       
      } catch (error) {
        console.error('Error creating custom select for:', select.id, error);
      }
    }
  });
  
  // Add a second attempt after a delay to catch any timing issues
  setTimeout(() => {
    const remainingSelects = document.querySelectorAll('select:not(.original-select)');
    if (remainingSelects.length > 0) {
      console.log('Found remaining selects to convert:', remainingSelects.length);
      remainingSelects.forEach(select => {
        console.log('Converting remaining select:', select.id);
        try {
          new CustomSelect(select);
        } catch (error) {
          console.error('Error converting remaining select:', select.id, error);
        }
      });
    }
  }, 500);
}

// Refresh custom select after options are updated
function refreshCustomSelect(selectId) {
  const select = document.getElementById(selectId);
  if (select && select.classList.contains('original-select')) {
    // Get the CustomSelect instance from the original select
    const instance = select._customSelectInstance;
    if (instance) {
      instance.updateFromOriginal();
    }
  }
}

// ✅ Persist state across ItemChanged
let state = {
  user: null,
  company: null,
};

Office.onReady(async () => {
  provider = new BubbleProvider();

  await loadUsers(provider);
  await loadInsuranceCompanies(provider);

  // Initialize custom selects after data is loaded and DOM is ready
  setTimeout(() => {
    initializeCustomSelects();
    
  }, 100);

  // Dropdowns
  document.getElementById("userSelect").addEventListener("change", onUserChange);
  document.getElementById("companySelect").addEventListener("change", onCompanyChange);

  // Folder modal
  document.getElementById("addFolderbtn").addEventListener("click", openAddFolderModal);
  document.getElementById("closeFolderModal").addEventListener("click", closeAddFolderModal);
  document.getElementById("createFolderBtn").addEventListener("click", onCreateFolder);

  // Contact modal
  document.getElementById("addContactbtn").addEventListener("click", openAddContactModal);
  document.getElementById("closeContactModal").addEventListener("click", closeAddContactModal);
  document.getElementById("cancelContactBtn").addEventListener("click", closeAddContactModal);
  document.getElementById("createContactBtn").addEventListener("click", onCreateContact);

  // Archive buttons
  document.getElementById("archiveBtn").addEventListener("click", () => handleArchive("archive"));
  document
    .getElementById("archiveDeleteBtn")
    .addEventListener("click", () => handleArchive("delete"));

  // ✅ Single ItemChanged handler
  Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, updateEmailDetails);

  // Initial load
  updateEmailDetails();
});

// ---- Users ----
async function loadUsers(provider) {
  showLoader();
  const select = document.getElementById("userSelect");
  if (!select) return;

  try {
    const users = await provider.getAllUsers();
    populateDropdown("userSelect", users);

    // ✅ Restore selected user
    if (state.user) {
      select.value = state.user;
      refreshCustomSelect("userSelect");
    }
  } catch (err) {
    console.error("Error loading users:", err);
    alert("Kunne ikke indlæse Key Accounts.");
  } finally {
    hideLoader();
  }
}

async function onUserChange(event) {
  const userKey = event.target.value;

  resetDropdown("companySelect");
  resetDropdown("folderSelect");
  resetDropdown("contactSelect");

  if (!userKey) return;

  showLoader();

  try {
    const companies = await provider.getAllCompanies(userKey);
    populateDropdown("companySelect", companies);

    selectedUser = userKey;
    state.user = userKey; // ✅ persist
  } catch (err) {
    console.error("Error loading companies:", err);
    alert("Kunne ikke indlæse virksomheder.");
  } finally {
    hideLoader();
  }
}

async function onCompanyChange(event) {
  const companyKey = event.target.value;

  resetDropdown("folderSelect");
  resetDropdown("contactSelect");

  if (!companyKey) return;

  showLoader();

  try {
    const folders = await provider.getAllFolders(companyKey);
    populateDropdown("folderSelect", folders);

    const contacts = await provider.getAllContacts(companyKey);
    populateDropdown("contactSelect", contacts);

    selectedCompany = companyKey;
    state.company = companyKey; // ✅ persist
  } catch (err) {
    console.error("Error loading folders/contacts:", err);
    alert("Kunne ikke indlæse virksomheder.");
  } finally {
    hideLoader();
  }
}

// ---- Insurance Companies ----
async function loadInsuranceCompanies(provider) {
  showLoader();

  const select = document.getElementById("insuranceSelect");
  if (!select) return;

  try {
    const insuranceCompanies = await provider.getAllInsuranceCompanies();
    populateDropdown("insuranceSelect", insuranceCompanies);
  } catch (err) {
    console.error("Error loading insurance companies:", err);
    alert("Kunne ikke indlæse forsikringsselskaber.");
  } finally {
    hideLoader();
  }
}

// ---- Folder Modal ----
function openAddFolderModal() {
  const companyKey = document.getElementById("companySelect").value;

  if (!companyKey) {
    showMessage("Please select a company first!", "error");
    return;
  }

  document.getElementById("addFolderModal").style.display = "flex";
}

function closeAddFolderModal() {
  document.getElementById("addFolderModal").style.display = "none";
  document.getElementById("folderNameInput").value = "";
}

async function onCreateFolder() {
  const folderName = document.getElementById("folderNameInput").value.trim();
  const companyKey = document.getElementById("companySelect").value;

  if (!companyKey) {
    showMessage("Please select a company first!", "error");
    return;
  }

  if (!folderName) {
    showMessage("Please enter a folder name!", "error");
    return;
  }

  showModalLoader("folderModalLoader");

  try {
    const newFolder = await provider.addFolderToBubble({
      Company: companyKey,
      Name: folderName,
    });

    if (newFolder) {
      showMessage("Folder created successfully!", "success");

      await onCompanyChange({ target: { value: companyKey } });

      const folderSelect = document.getElementById("folderSelect");
      if (folderSelect) {
        folderSelect.value = newFolder.key;
        folderSelect.dispatchEvent(new Event("change"));
        refreshCustomSelect("folderSelect");
      }

      closeAddFolderModal();
    }
  } catch (err) {
    console.error("Error creating folder:", err);
    showMessage("Error creating folder.", "error");
  } finally {
    hideModalLoader("folderModalLoader");
  }
}

// ---- Contact Modal ----
function openAddContactModal() {
  const companyKey = document.getElementById("companySelect").value;

  if (!companyKey) {
    showMessage("Please select a company first!", "error");
    return;
  }

  document.getElementById("addContactModal").style.display = "flex";
}

function closeAddContactModal() {
  document.getElementById("addContactModal").style.display = "none";
  document.getElementById("contactTitleInput").value = "";
  document.getElementById("contactNameInput").value = "";
  document.getElementById("contactEmailInput").value = "";
  document.getElementById("contactPhoneInput").value = "";
}

async function onCreateContact() {
  const companyKey = document.getElementById("companySelect").value;

  if (!companyKey) {
    showMessage("Please select a company first!", "error");
    return;
  }

  const title = document.getElementById("contactTitleInput").value.trim();
  const name = document.getElementById("contactNameInput").value.trim();
  const email = document.getElementById("contactEmailInput").value.trim();
  const phone = document.getElementById("contactPhoneInput").value.trim();

  if (!name || !email) {
    showMessage(`${!name ? "Name " : ""}${!email ? "Email " : ""}cannot be empty!`, "error");
    return;
  }

  const fields = { Name: name, Company: companyKey, email, phone, Titel: title };

  showModalLoader("contactModalLoader");

  try {
    const newContact = await provider.addContactToBubble(fields);

    if (newContact) {
      showMessage("Contact created successfully!", "success");

      await onCompanyChange({ target: { value: companyKey } });

      const contactSelect = document.getElementById("contactSelect");
      if (contactSelect) {
        contactSelect.value = newContact.key;
        contactSelect.dispatchEvent(new Event("change"));
        refreshCustomSelect("contactSelect");
      }

      closeAddContactModal();
    }
  } catch (err) {
    console.error("Error creating contact:", err);
    showMessage("Error creating contact.", "error");
  } finally {
    hideModalLoader("contactModalLoader");
  }
}

// ---- Email Details ----
async function updateEmailDetails() {
  const item = Office.context.mailbox.item;
  if (!item) return;

  // Reset dot immediately while we check
  setStatusDot("idle");

  const subjectInput = document.getElementById("subjectInput");
  if (subjectInput) subjectInput.value = item.subject || "";

  const senderContainer = document.getElementById("senderContainer");
  if (senderContainer) {
    let fromEmail = "";

    if (item.itemType === Office.MailboxEnums.ItemType.Message && item.from) {
      fromEmail = item.from.emailAddress;
    } else if (item.itemType === Office.MailboxEnums.ItemType.Appointment && item.organizer) {
      fromEmail = item.organizer.emailAddress;
    }

    if (!fromEmail && item.sender) fromEmail = item.sender.emailAddress;

    senderContainer.textContent = `Afsender: ${fromEmail}`;
  }

  const dateContainer = document.getElementById("dateContainer");
  if (dateContainer && item.dateTimeCreated) {
    dateContainer.textContent = `Dato: ${formatDate(item.dateTimeCreated)}`;
  }

  if (provider) {
    try {
      // Set dot to "checking" state
      setStatusDot("idle");

      // Fetch the full record (null if not archived yet)
      const record = await provider.getEmailFromBubble(item);

      if (record) {
        setStatusDot("exists");
        // await updateRibbonIcon(true);
        await preFillFromRecord(record);
      } else {
        setStatusDot("missing");
        // await updateRibbonIcon(false);
        resetPrefillSelection();
      }
    } catch (err) {
      console.error("Error fetching email record:", err);
      setStatusDot("idle");
    }
  }
}

/**
 * Pre-fills all dropdowns from an existing Bubble email record.
 * Mirrors C# PreFillFromRecord — temporarily detaches change handlers
 * so cascading loads don't fire twice.
 */
async function preFillFromRecord(record) {
  // Helper: read a top-level field from the raw Bubble record
  const getField = (key) => {
    const val = record[key];
    return val != null ? String(val) : null;
  };

  const userId        = getField("Key Account");
  const companyId     = getField("Company");
  const folderId      = getField("Folder");
  const contactId     = getField("Company contact");
  const insuranceId   = getField("Insurance company");
  const isBusinessCritical = getField("isBackofficeKeyAccount");

  // Detach change handlers while we populate to avoid cascading fetches
  const userSelect    = document.getElementById("userSelect");
  const companySelect = document.getElementById("companySelect");

  userSelect.removeEventListener("change", onUserChange);
  companySelect.removeEventListener("change", onCompanyChange);

  try {
    showLoader();

    // ---- User ----
    if (userId) {
      userSelect.value = userId;
      refreshCustomSelect("userSelect");
      selectedUser = userId;
      state.user = userId;

      // Load companies for this user so the company dropdown is populated
      const companies = await provider.getAllCompanies(userId);
      populateDropdown("companySelect", companies);
    }

    // ---- Company ----
    if (companyId) {
      companySelect.value = companyId;
      refreshCustomSelect("companySelect");
      selectedCompany = companyId;
      state.company = companyId;

      // Load folders and contacts for this company
      const [folders, contacts] = await Promise.all([
        provider.getAllFolders(companyId),
        provider.getAllContacts(companyId),
      ]);
      populateDropdown("folderSelect",   folders);
      populateDropdown("contactSelect",  contacts);
    }

    // ---- Folder ----
    if (folderId) {
      const folderSelect = document.getElementById("folderSelect");
      if (folderSelect) {
        folderSelect.value = folderId;
        refreshCustomSelect("folderSelect");
      }
    }

    // ---- Contact ----
    if (contactId) {
      const contactSelect = document.getElementById("contactSelect");
      if (contactSelect) {
        contactSelect.value = contactId;
        refreshCustomSelect("contactSelect");
      }
    }

    // ---- Insurance ----
    if (insuranceId) {
      const insuranceSelect = document.getElementById("insuranceSelect");
      if (insuranceSelect) {
        insuranceSelect.value = insuranceId;
        refreshCustomSelect("insuranceSelect");
      }
    }

    // ---- Business critical checkbox ----
    const criticalCheckbox = document.getElementById("critical");
    if (criticalCheckbox) {
      criticalCheckbox.checked =
        isBusinessCritical != null &&
        isBusinessCritical.toLowerCase() === "true";
    }
  } catch (err) {
    console.error("preFillFromRecord failed:", err);
  } finally {
    hideLoader();
    // Re-attach change handlers
    userSelect.addEventListener("change", onUserChange);
    companySelect.addEventListener("change", onCompanyChange);
  }
}

/**
 * Clears all dropdowns and the checkbox back to their default empty state.
 * Mirrors C# ResetPrefillSelection.
 */
function resetPrefillSelection() {
  const userSelect    = document.getElementById("userSelect");
  const companySelect = document.getElementById("companySelect");

  // Detach handlers so resetting values doesn't trigger cascading loads
  userSelect.removeEventListener("change", onUserChange);
  companySelect.removeEventListener("change", onCompanyChange);

  try {
    ["userSelect", "companySelect", "folderSelect", "contactSelect", "insuranceSelect"].forEach(
      (id) => {
        const sel = document.getElementById(id);
        if (sel) {
          sel.value = "";
          refreshCustomSelect(id);
        }
      }
    );

    const criticalCheckbox = document.getElementById("critical");
    if (criticalCheckbox) criticalCheckbox.checked = false;

    selectedUser    = null;
    selectedCompany = null;
    state.user      = null;
    state.company   = null;
  } catch (err) {
    console.error("resetPrefillSelection failed:", err);
  } finally {
    userSelect.addEventListener("change", onUserChange);
    companySelect.addEventListener("change", onCompanyChange);
  }
}

// ---- Ribbon ----
async function updateRibbonIcon(exists) {
  try {
    if (!Office.context.requirements.isSetSupported("Ribbon", "1.1")) {
      console.warn("[Ribbon] Dynamic ribbon updates not supported.");
      return;
    }

    const prefix = exists ? "IconGreen" : "IconRed";

    await Office.ribbon.requestUpdate({
      tabs: [
        {
          id: "TabDefault",
          groups: [
            {
              id: "msgReadGroup",
              controls: [
                {
                  id: "msgReadOpenPaneButton",
                  icon: [
                    { size: 16, resid: `${prefix}16` },
                    { size: 32, resid: `${prefix}32` },
                    { size: 80, resid: `${prefix}80` },
                  ],
                },
              ],
            },
          ],
        },
      ],
    });

    console.log("Ribbon updated");
  } catch (err) {
    console.error("Error updating ribbon icon:", err);
  }
}

// ---- Archive ----
async function handleArchive(type) {
  const item = Office.context.mailbox.item;
  if (!item) return;

  const userKey = document.getElementById("userSelect").value;
  const companyKey = document.getElementById("companySelect").value;

  if (!userKey || !companyKey) {
    showMessage("Vælg venligst Key Account og Virksomhed.", "error");
    return;
  }

  showLoader();

  try {
    const mailModel = {
      mailItem: item,
      subject: document.getElementById("subjectInput").value,
      userId: userKey,
      company: companyKey,
      folder: document.getElementById("folderSelect").value,
      contact: document.getElementById("contactSelect").value,
      insuranceCompany: document.getElementById("insuranceSelect").value,
      isBusinessCritical: document.getElementById("critical").checked,
    };

    const exists = await provider.doesEmailExist(item);

    if (exists) {
      const result = await utility.ReUploadMessage(
        "Email already archived, do you want to archive again?"
      );
      if (!result) return false;
    }

    const success = await provider.saveEmail(mailModel, type);

    if (success) {
      showMessage("Email arkiveret med succes!", "success");
    //   await updateRibbonIcon(true);
    } else {
      showMessage("Fejl under arkivering af email.", "error");
    }
  } catch (err) {
    console.error("Error in handleArchive:", err);
    showMessage("Der opstod en fejl.", "error");
  } finally {
    hideLoader();
  }
}

// ---- Helpers ----
function populateDropdown(selectId, items) {
  const select = document.getElementById(selectId);
  if (!select) return;

  select.innerHTML = '<option value=""></option>';

  items.forEach((item) => {
    const opt = document.createElement("option");
    opt.value = item.key;
    opt.textContent = item.value;
    select.appendChild(opt);
  });

  // Initialize custom select if it doesn't exist, otherwise refresh it
  if (!select.classList.contains('original-select')) {
    new CustomSelect(select);
  } else {
    refreshCustomSelect(selectId);
  }
}


function resetDropdown(selectId) {
  const select = document.getElementById(selectId);
  if (select) {
    select.innerHTML = '<option value=""></option>';
    refreshCustomSelect(selectId);
  }
}

function showLoader() {
  const loader = document.querySelector(".loader");
  if (loader) loader.style.display = "inline-block";
}

function hideLoader() {
  const loader = document.querySelector(".loader");
  if (loader) loader.style.display = "none";
}

function showModalLoader(loaderId) {
  const loader = document.getElementById(loaderId);
  if (loader) loader.style.display = "flex";
}

function hideModalLoader(loaderId) {
  const loader = document.getElementById(loaderId);
  if (loader) loader.style.display = "none";
}

function showMessage(message, type = "info") {
  const container = document.getElementById("messageContainer");
  if (!container) return;

  container.textContent = message;
  container.className = `message ${type}`;
  container.style.display = "block";

  setTimeout(() => {
    container.style.display = "none";
  }, 3000);
}

function formatDate(date) {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");

  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
}

/**
 * Updates the header status dot.
 * @param {"idle"|"exists"|"missing"} state
 */
function setStatusDot(state) {
  const dot = document.getElementById("emailStatusDot");
  if (!dot) return;

  dot.classList.remove("status-idle", "status-exists", "status-missing");

  const titles = {
    idle:    "Checking email status...",
    exists:  "Email already archived",
    missing: "Email not yet archived",
  };

  dot.classList.add(`status-${state}`);
  dot.title = titles[state] || "";
}
